/**
 * The range-analytics read — slice 14's whole client surface.
 *
 *   fetchAnalyticsReport   GET /analytics/range   what went out between two dates
 *
 * **One request, and every figure in the report comes from it.** (The one
 * card on the screen that is not the report — slice 44's Meta ad account — is
 * a second read, of Meta, through `meta-ads.ts`, and carries its own tag.) The same
 * argument `overview.ts` makes for the dashboard applies here and applies harder:
 * a range report is an aggregate over a period, and a screen assembled from
 * `/campaigns`, `/campaigns/:id/results` and `/campaigns/:id/whatsapp` would be
 * one request per campaign — the N+1 this codebase calls out by name — computed
 * at as many different moments as there are campaigns. Worse, it could not
 * produce the headline at all: distinct *recipients* across the range is
 * one distinct count of people (the owners of the recipients' keys, since slice
 * 58) over one set of rows, and no
 * amount of client arithmetic over per-campaign responses recovers it. Adding
 * two campaigns' reach counts everybody in both of them twice. The sum belongs
 * where the rows are, so `generatedAt` on the response is one moment and means it.
 *
 * **It is a `GET` with a query, not a `POST` with a body**, because it reads and
 * creates nothing — 201 would be a lie and a report here is not a run. That is
 * the visible difference from slice 13 next door: `POST /reports/runs` records
 * something that outlives the request, and this returns a computation that does
 * not exist afterwards. `analyticsReportSchema` says so itself — *computed on
 * read, never stored*.
 *
 * **The three query parameters are re-validated here before anything leaves the
 * browser**, with the contract the API validates against, which is the same
 * habit every other client file in this app follows. What it buys on this
 * endpoint specifically: `analyticsRangeRequestSchema` is where a reversed range,
 * an impossible day and a period over `MAX_REPORT_WINDOW_DAYS` are refused, and
 * where **`timezone` has no default**. A request that forgot the zone must not
 * reach the API and come back bucketed against the server's clock — "last month"
 * read in UTC for a tenant in IST moves five and a half hours of sends into the
 * wrong bucket and nothing on the resulting screen looks wrong.
 *
 * **The dates go over the wire as calendar dates, not as instants**, and that is
 * deliberate: `2026-09-30` is not a moment until somebody says *where*, and
 * sending resolved instants would let this browser pick the zone by accident —
 * its own — while the report printed the tenant's. The API resolves them once,
 * through `resolveAnalyticsRange`, and echoes the resolved window back as
 * `report.range` with `report.rangeLabel` beside it, so the screen prints the
 * period the API actually summed rather than the one a component believed it
 * asked for.
 *
 * **The response parse is a real guard on this endpoint, not ceremony.**
 * `analyticsReportSchema` is `.strict()` over a shape whose whole point is what
 * it refuses: `crossChannelTotalSchema` carries a `measure` from
 * `['accepted', 'delivered']`, so a combined *failure* total invented by a mapper
 * on the API side fails here, in this file, rather than arriving on screen as a
 * number that tells a tenant to clean 411 working numbers off their list. The
 * nullable `email` and `whatsapp` are checked the same way — `null` means this
 * workspace does not send on that channel, and it is not interchangeable with a
 * zero-filled object.
 *
 * **Nothing here names a tenant and no parameter could.** The query carries two
 * dates and a zone; the API fills the workspace from the authenticated principal
 * and RLS enforces it underneath. And nothing here can send: this is a read over
 * campaigns and delivery events that already happened.
 *
 * The route name follows the contract's own `GET /analytics/range` and the
 * `@Controller('<noun>')`-plus-a-path convention the other modules use. If it
 * moves, this file is the only place in this app that has to change.
 */
import {
  analyticsRangeRequestSchema,
  analyticsReportResponseSchema,
  type AnalyticsRangeRequest,
  type AnalyticsReport,
} from '@wend/shared';

import { apiRequest } from './client';

/**
 * Everything the range screen's report draws, in one read.
 *
 * Takes the request already shaped by the contract and parses it again anyway —
 * cheap, and it means a caller that built the object by hand cannot skip the
 * zone or send a reversed range. The `AbortSignal` is `useAsyncData`'s: changing
 * the period while a read is in flight aborts the old one rather than racing two
 * responses onto one screen, which on this page would mean a range label from
 * one period beside counts from another.
 */
export async function fetchAnalyticsReport(
  request: AnalyticsRangeRequest,
  signal?: AbortSignal,
): Promise<AnalyticsReport> {
  const query = analyticsRangeRequestSchema.parse(request);

  const response = await apiRequest({
    path: '/analytics/range',
    query: {
      fromDate: query.fromDate,
      toDate: query.toDate,
      timezone: query.timezone,
    },
    schema: analyticsReportResponseSchema,
    signal,
  });

  return response.report;
}
